import { connect, ConnectedProps } from 'react-redux'
import InputTableLeftColumn, { InputTableLeftColumnCompleteRequiredProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn'
import withLoading from '../../../../../../../../../../utils/hoc/withLoading'
import { ComponentTabObject } from '../../../../../../../../../../utils/hooks/useTabLogic'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../../../../../utils/reducers'
import { CostBuyPayload } from '../../../../../../../../../../utils/reducers/company/costsBuy'
import { BuyTagsPayload } from '../../../../../../../../../../utils/reducers/buys/buyTags'
import BuyReadOnlyHeaderMain from '../../../../../header/BuyReadOnlyHeaderMain'
import BuyReadOnlyHeaderFinancialRows from '../../../../../header/BuyReadOnlyHeaderFinancialRows'
import BuyReadOnlyHeaderCosts from '../../../../../header/BuyReadOnlyHeaderCosts'
import BuyRealOnlyHeaderAccounts from '../../../../../header/BuyReadOnlyHeaderAccounts'
import useBuyDeliveryNoteHeaderURLs from '../../../../../../../../../../utils/hooks/buys/orders/useBuyDeliveryNoteHeaderURLs'
import BuyReadOnlyHeaderShipping from '../../../../../header/BuyReadOnlyHeaderShipping'
import BuyReadOnlyHeaderVariables from '../../../../../header/BuyReadOnlyHeaderVariables'
import { SellVariablePayload } from '../../../../../../../../../../utils/reducers/company/sellVariables'
import AppStyleDecider from '../../../../../../../../../../baseComponents/AppsStyleDecider'
import InputTableTopHeader from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableTopHeader'
import ITHeaderText, { ITHeaderTextCustomer } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { parseAddressToOneLine } from '../../../../../../../../../../utils/globals/address'
import { displayDate } from '../../../../../../../../../../utils/globals/date'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../utils/globals/tailwind'
import { ITHeaderBuyOrderTagsNew } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components'
import { InputWithLabelMargin } from '../../../../../../../../../../forms/Input'
import { BuyOrderModelType } from '@teinor/erp/types/company/order/buy/buyOrder'
import { FormElement, FormRender } from '../../../../../../../../../../forms/FormRenderer/types'
import { useOrderITHeaderHandleChangeLogic } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/useOrderITHeaderHandleChange'
import { getActiveCurrenciesDictionary } from '../../../../../../../../../../utils/reducers/getters'
import { ProductTypesPayload } from '../../../../../../../../../../utils/reducers/products/productTypes'

type BuyDeliveryNoteFinishedStepITHeaderProps = InputTableLeftColumnCompleteRequiredProps & ReduxBuyDeliveryNoteFinishedStepITHeader & {}

const BuyDeliveryNoteFinishedStepITHeader: React.FC<BuyDeliveryNoteFinishedStepITHeaderProps> = ({ ...rest }) => {
    return <AppStyleDecider DefaultComponent={BuyDeliveryNoteFinishedStepITHeaderDefault} NewComponent={BuyDeliveryNoteFinishedStepITHeaderNew} {...rest} />
}

const BuyDeliveryNoteFinishedStepITHeaderDefault: React.FC<BuyDeliveryNoteFinishedStepITHeaderProps> = ({ buyTags, ...rest }) => {
    const { t } = useTwinTranslation()
    const orderHeaderURLs = useBuyDeliveryNoteHeaderURLs()
    const tabs: ComponentTabObject = {
        'main': {
            component: BuyReadOnlyHeaderMain,
            text: t('dataDeliveryNote', 'Datos del albarán'), 
            extraProps: { orderHeaderURLs, buyTags }

        },
        'financialRow': {
            component: BuyReadOnlyHeaderFinancialRows,
            text: t('discountsAndRecharges', 'Descuentos y recargos'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'costs': {
            component: BuyReadOnlyHeaderCosts,
            text: t('costsDeliveryNote', 'Gastos del albarán'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'shipping': {
            component: BuyReadOnlyHeaderShipping,
            text: t('shipping', 'Transporte'),
            permission: 'buy.deliveryNote',
            extraProps: {
                orderHeaderURLs
            }
        },
        'accounts': {
            component: BuyRealOnlyHeaderAccounts,
            text: t('divideIntoChapters', 'Dividir en capítulos'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
        'variables': {
            component: BuyReadOnlyHeaderVariables,
            text: t('buyVariables', 'Variables de compra'),
            permission: 'buy.deliveryNote',
            extraProps: { orderHeaderURLs }
        },
    }
    return (
        <InputTableLeftColumn tabs={tabs} {...rest} />
    )
}

const BuyDeliveryNoteFinishedStepITHeaderNew: React.FC<BuyDeliveryNoteFinishedStepITHeaderProps> = ({ headerData, buyTags, setCompleteData, updateDataURL, getCompleteData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData, updateDataURL })
    const activeCurrencies = getActiveCurrenciesDictionary()
    const extraCustomer: FormElement<keyof BuyOrderModelType>[] = []
    if (headerData?.CustomerId) {
        extraCustomer.push(
            {
                name: 'CustomerId',
                component: 'Custom',
                render: () => <ITHeaderTextCustomer label={t('customer', 'Cliente')} headerData={headerData} />,
                hideSectionByDefault: true
            },
        )
    }

    const sections: FormRender<BuyOrderModelType> = [
        {
            cols: 7,
            className: 'gap-x-20',
            elements: [
                {
                    name: 'custom_name',
                    component: 'Custom',
                    render: (props) => {
                        return <InputWithLabelMargin name='custom_name' label={t('customName', 'Nombre personalizado')} onChange={props.onChange} defaultValue={headerData?.custom_name} className='custom_name_input_field' />
                    }
                },
                {
                    name: 'processed_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateBuyOrder', 'Fecha de la compra')} value={value ? displayDate(new Date(value)) : ''} />
                },
                {
                    name: 'RepresentativeEmployeeId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('representative', 'Representante')} value={headerData?.RepresentativeEmployee?.fullname_short} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'OrderTags',
                    component: 'Custom',
                    render: ({ extraProps }) => {
                        return (<ITHeaderBuyOrderTagsNew getCompleteData={getCompleteData} selectedTags={extraProps.selectedTags} dictTags={extraProps.buyTags} headerData={headerData || {}} onChange={handleChange} {...rest} className={returnClassOfSpanTemplate(3) + ' order_top_header_custom_select_tags'} />)
                    },
                    extraProps: {
                        selectedTags: headerData?.tags,
                        buyTags
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'full_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('assignedNumber', 'Nº asignado')} value={value} className={(headerData?.assigned_number ? '' : 'text-red-BA')} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'supplier_number_order',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierNumber', 'Número del proveedor')} value={value} />,
                },
                {
                    name: 'supplier_assigned_number',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('supplierInvoiceS', 'Fact. proveedor')} value={value} />,
                },
                {
                    name: 'supplier_date',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('dateSupplier', 'Fecha del proveedor')} value={value ? displayDate(new Date(value)) : ''} />,
                },
                ...extraCustomer,
                {
                    name: 'BillingWorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('billedBy', 'Facturado por')} value={headerData?.BillingWorkingCenter?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterCounterId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('serie', 'Serie')} value={headerData?.WorkingCenterCounter?.serie} />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'WorkingCenterId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('buyedBy', 'Comprado por')} value={headerData?.WorkingCenter.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'WarehouseId',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('warehouse', 'Almacen')} value={headerData?.Warehouse?.name || ''} />,
                    hideSectionByDefault: true
                },
                {
                    name: 'BillingAddressId',
                    component: 'Custom',
                    render: () => {
                        return <ITHeaderText label={t('workingCenterAddress', 'Dirección centro de trabajo')} value={parseAddressToOneLine(headerData?.BillingWorkingCenter)
                        } />
                    },
                    hideSectionByDefault: true
                },
                {
                    name: 'CurrencyId',
                    component: 'Custom',
                    render: ({ value }) => <ITHeaderText label={t('currency', 'Moneda')} value={activeCurrencies?.[value]?.name} />,
                    hideSectionByDefault: true
                },
            ]
        }
    ]

    return (
        <InputTableTopHeader handleChange={handleChange} sections={sections} headerData={headerData} getCompleteData={getCompleteData} />
    )
}

const BuyDeliveryNoteFinishedStepITHeaderDispatch = {
    setCostsBuy: (payload: CostBuyPayload) => ({ type: 'CHANGE_COSTBUY', payload }),
    setBuyTags: (payload: BuyTagsPayload) => ({ type: 'CHANGE_BUY_TAGS', payload }),
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxBuyDeliveryNoteFinishedStepITHeader = ConnectedProps<typeof buyDeliveryNoteFinishedStepITHeaderConnect>
const mapBuyDeliveryNoteFinishedStepITHeaderConnector = (state: AllReduxPayloads) => ({ costsBuy: state.costsBuy, buyTags: state.buyTags, sellVariables: state.sellVariables, productTypes: state.productTypes })
const buyDeliveryNoteFinishedStepITHeaderConnect = connect(mapBuyDeliveryNoteFinishedStepITHeaderConnector, BuyDeliveryNoteFinishedStepITHeaderDispatch)
const BuyDeliveryNoteFinishedStepITHeaderConnectLoading = withLoading(BuyDeliveryNoteFinishedStepITHeader, [{ fetchUrl: '/api/app/config/cost/costBuy/getAllCostBuys', propName: 'costsBuy', setFunctionName: 'setCostsBuy' }, { fetchUrl: '/api/app/order/buy/tag/getAllBuyTags', propName: 'buyTags', setFunctionName: 'setBuyTags' }, { fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])
const BuyDeliveryNoteFinishedStepITHeaderConnect = buyDeliveryNoteFinishedStepITHeaderConnect(BuyDeliveryNoteFinishedStepITHeaderConnectLoading)

export default BuyDeliveryNoteFinishedStepITHeaderConnect